import { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { LoadingComponent } from '@blaze-cms/react-page-builder';
import { checkIfLoggedIn } from '@blaze-cms/core-auth-ui';
import { useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { useToasts } from '@blaze-react/toaster';
import FormInput from './FormInput';
import { UPDATE_USER } from '../../services/mutations';
import { GET_ACCOUNT_USER } from '../../services/querys';
import { FORM_SCHEMA, CLASSNAME } from './constants';

const UpdateUserAccount = ({ modifier }) => {
  const { id: userId } = checkIfLoggedIn();

  const { addToast } = useToasts();
  const { data = {}, loading: loadingUser } = useQuery(GET_ACCOUNT_USER, {
    variables: { id: userId },
    skip: !userId,
    fetchPolicy: 'network-only'
  });
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showTick, setShowTick] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    getValues,
    reset
  } = useForm({
    resolver: ajvResolver(FORM_SCHEMA, { strict: false, $data: true, allErrors: true }),
    mode: 'onSubmit',
    reValidateMode: 'onChange'
  });

  useEffect(() => {
    if (!loadingUser && data.user) {
      const { shopwareProperties = {} } = data.user;
      reset(
        {
          tel: shopwareProperties.tel || '',
          password: '',
          passwordConfirm: ''
        },
        { keepDefaultValues: false }
      );
    }
  }, [data, loadingUser, reset]);

  const [updateUser, { loading }] = useMutation(UPDATE_USER, {
    onCompleted: async () => {
      // Clear password fields after successful update
      const currentValues = getValues();
      reset({
        ...currentValues,
        password: '',
        passwordConfirm: ''
      });
      setShowErrorMessage(false);
      setShowTick(true);
      addToast('Account details updated successfully', {
        appearance: 'success',
        autoDismiss: true
      });
      setTimeout(() => setShowTick(false), 2500);
    },
    onError: error => {
      setShowErrorMessage(error.message);
      addToast('Failed to update account details', {
        appearance: 'error',
        autoDismiss: true
      });
    }
  });

  const handleSubmitForm = formData => {
    const userInput = {};

    // Only include tel if it's provided
    if (formData.tel) {
      userInput.tel = formData.tel;
    }

    // Only include password if it's provided
    if (formData.password) {
      userInput.password = formData.password;
    }

    // Only update if there's something to update
    if (Object.keys(userInput).length > 0) {
      updateUser({ variables: { input: { user: userInput } } });
    }
  };

  if (loadingUser) return <LoadingComponent />;

  if (!userId) {
    return (
      <div className={`${CLASSNAME}${modifier ? ` ${modifier}` : ''}`}>
        <p className="logged-out-message">Please log in to update your account details.</p>
      </div>
    );
  }

  const parsedModifier = modifier ? ` ${modifier}` : '';
  const formFields = Object.values(FORM_SCHEMA.properties);

  const getButtonContent = () => {
    if (showTick) return 'Saved';
    if (loading) return <LoadingComponent />;
    return 'Update Account';
  };

  return (
    <div className={`form-container${parsedModifier}`}>
      <form onSubmit={handleSubmit(handleSubmitForm)} className="blaze-user-form">
        {formFields.map(field => {
          if (!field.showInForm) return null;
          return (
            <div className="blaze-user-form__input-group" key={field.fieldId}>
              <FormInput
                id={field.fieldId}
                label={field.label}
                isRequired={field.isRequired}
                formFieldType={field.formFieldType}
                register={register}
                placeholder={field.placeholder}
                error={errors[field.fieldId]}
              />
            </div>
          );
        })}

        <button
          className={`button blaze-user-form__submit-button ${
            !isDirty ? 'blaze-user-form__submit-button--condensed' : ''
          }`}
          type="submit"
          disabled={showTick || loading}>
          {getButtonContent()}
        </button>

        {showErrorMessage && (
          <div className="form error_message_wrapper">
            <div className="error_message_icon" />
            <div className="error_message">{showErrorMessage}</div>
          </div>
        )}
      </form>
    </div>
  );
};

export default UpdateUserAccount;
